// =============================================================================
// Edytor układu wydruku — spina płótno, warstwy, właściwości i serwer.
//
// TRZY LICZBY WERSJI, ŁATWE DO POMYLENIA (gramatyka mówi o tym wprost):
//   • `layoutVersion`       — wersja GRAMATYKI,
//   • `baseTemplateVersion` — wersja wzoru w chwili zasiewu; rozjazd
//                             z katalogiem to chip „wymaga przeglądu",
//   • `version` (w bazie)   — licznik PUBLIKACJI. To JEGO odsyłamy jako
//                             `expected_version`.
//
// SZKIC I PUBLIKACJA TO DWIE RÓŻNE RZECZY. Autozapis leci do `…/uklad/szkic`
// i NIE podbija `version` — praca w toku nie jest publikacją. Dopiero
// „Opublikuj" robi warunkowy UPDATE po `expected_version`; gdy ktoś w drugiej
// karcie opublikował wcześniej, wracamy z 409 i `current_version`, zamiast po
// cichu nadpisać cudzą robotę.
//
// SZKIC SPRAWDZA TYLKO GRAMATYKĘ. Niezwiązana zmienna nieopcjonalna ani blok
// wystający poza etykietę nie blokują zapisu — blokują publikację. Odwrotnie
// byłoby okrutne: odmowa zapisu kasuje robotę, której nikt jeszcze nie skończył.
//
// OKNO JEST NARZĘDZIEM, NIE FORMULARZEM — i to jest decyzja o układzie, nie
// o wyglądzie. Do 2026-08-26 edytor siedział w dialogu `size="xl"`, czyli
// `max-w-4xl` = 896 px, na monitorze 1920 px. W trzech kolumnach
// `220px / 1fr / 280px` zostawało na płótno ~340 px, więc etykieta 80×50 mm
// przy sztywnych 3 px/mm była znaczkiem pocztowym pośrodku pustego pola,
// a nazwy warstw czytały się „T..", „P..", „L..". Dialog bierze więc całą
// dostępną powierzchnię, płótno DOPASOWUJE się do niej samo, a podgląd
// przeniósł się do zakładki, żeby nie odbierać płótnu wysokości.
// =============================================================================

import type { LayoutNode, PrintLayout } from '@insolitum/print-templates';
import { Alert, Badge, Button, Modal, ModalFooter, Spinner, cn } from '@insolitum/ui';
import { AlertTriangle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  komunikatLimituPlanu,
  PrintCoreError,
  opublikujUklad,
  plikDoBase64,
  pobierzUklad,
  pobierzZasoby,
  podgladUkladu,
  porzucSzkicUkladu,
  wgrajZasob,
  zapiszSzkicUkladu,
  zasiejUklad,
  type BladWezla,
  type SzablonKatalogu,
  type ZasobDruku,
} from '../printCoreApi';

import { PanelPodgladu, type StanPodgladu } from './PanelPodgladu';
import { PanelWarstw } from './PanelWarstw';
import { PanelWlasciwosci } from './PanelWlasciwosci';
import { PasekNarzedzi, type DoWstawienia } from './PasekNarzedzi';
import { Plotno, wymiaryMm } from './Plotno';
import {
  SKALA_MAX,
  SKALA_MIN,
  biezacy,
  cofnij,
  dopiszHistorie,
  mozeCofnac,
  mozePonowic,
  nowaHistoria,
  nowyWezel,
  ponow,
  przesunWezel,
  skalaDopasowania,
  usunWezel,
  wezelChwili,
  wezlyTabeli,
  zmienWezel,
  type Historia,
} from './stan';

/** Ile milisekund po ostatniej zmianie lecimy z autozapisem. */
const OPOZNIENIE_AUTOZAPISU = 1200;

type StanZapisu =
  | { rodzaj: 'spoczynek' }
  | { rodzaj: 'zapisuje' }
  | { rodzaj: 'zapisano' }
  | { rodzaj: 'blad'; komunikat: string };

/** Nagłówek kolumny bocznej — jeden kształt dla warstw i dla prawej strony. */
function NaglowekKolumny({ children }: { children: React.ReactNode }) {
  return (
    <div className="shrink-0 border-b border-aurora-border px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-aurora-muted">
      {children}
    </div>
  );
}

export function EdytorUkladu({
  orgId,
  szablon,
  onZamknij,
  onOpublikowano,
}: {
  orgId: string;
  szablon: SzablonKatalogu;
  onZamknij: () => void;
  onOpublikowano: () => void;
}) {
  const [historia, setHistoria] = useState<Historia | null>(null);
  const [zaznaczony, setZaznaczony] = useState<string | null>(null);
  const [wczytuje, setWczytuje] = useState(true);
  const [bladWczytania, setBladWczytania] = useState<string | null>(null);
  const [wersjaBazowa, setWersjaBazowa] = useState(0);
  const [drift, setDrift] = useState(false);
  const [niezwiazane, setNiezwiazane] = useState<string[]>([]);
  const [pozaObszarem, setPozaObszarem] = useState<BladWezla[]>([]);
  const [stanZapisu, setStanZapisu] = useState<StanZapisu>({ rodzaj: 'spoczynek' });
  const [publikuje, setPublikuje] = useState(false);
  const [bladPublikacji, setBladPublikacji] = useState<string | null>(null);
  const [zakladka, setZakladka] = useState<'wlasciwosci' | 'podglad'>('wlasciwosci');
  const [zasoby, setZasoby] = useState<ZasobDruku[]>([]);
  const [bladZasobu, setBladZasobu] = useState<string | null>(null);

  // Powiększenie: `auto` znaczy „licz z rozmiaru okna". Każde ręczne
  // powiększenie je wyłącza, „Dopasuj" włącza z powrotem — bo skala
  // przeskakująca użytkownikowi pod ręką przy zmianie rozmiaru okna byłaby
  // gorsza niż brak dopasowania w ogóle.
  const [skala, setSkala] = useState(3);
  const [auto, setAuto] = useState(true);
  const obszarPlotna = useRef<HTMLDivElement>(null);

  const [podglad, setPodglad] = useState<StanPodgladu | null>(null);
  const [bladPodgladu, setBladPodgladu] = useState<string | null>(null);
  const [odswiezaPodglad, setOdswiezaPodglad] = useState(false);
  const [celPodgladu, setCelPodgladu] = useState((szablon.targets ?? ['PDF'])[0] ?? 'PDF');

  const layout = historia ? biezacy(historia) : null;
  const licznikZapisu = useRef(0);

  // ── Wczytanie ──────────────────────────────────────────────────────────────
  const wczytaj = useCallback(async () => {
    setWczytuje(true);
    setBladWczytania(null);
    try {
      const stan = await pobierzUklad(orgId, szablon.slug);
      setWersjaBazowa(stan.version);
      setDrift(stan.drift);
      setNiezwiazane(stan.unbound_required);
      setPozaObszarem(stan.out_of_bounds ?? []);
      const zapisany = (stan.layout_draft ?? stan.layout) as PrintLayout | null;
      if (zapisany) {
        setHistoria(nowaHistoria(zapisany));
      } else {
        // Brak układu i brak szkicu — zasiew z katalogu robi serwer, bo to on
        // ma katalog. 422 `seed_not_faithful` znaczy „tego wzoru nie umiemy
        // zasiać bez zmiany wydruku" i wtedy edytor ma ODMÓWIĆ.
        const zasiew = await zasiejUklad(orgId, szablon.slug);
        setHistoria(nowaHistoria(zasiew.layout as PrintLayout));
      }
    } catch (err) {
      setBladWczytania(
        err instanceof PrintCoreError ? err.message : 'Nie udało się wczytać układu'
      );
    } finally {
      setWczytuje(false);
    }
  }, [orgId, szablon.slug]);

  useEffect(() => {
    void wczytaj();
  }, [wczytaj]);

  // Lista obrazów tenanta wczytuje się RAZ, obok układu: wybór pliku ma być
  // gotowy w chwili, w której ktoś kliknie węzeł obrazu, a nie dopiero po
  // rozwinięciu listy. Cichy `catch` jest tu właściwy — brak zasobów nie może
  // przykryć edytora błędem, a puste pole wyboru samo mówi, że nic nie ma.
  useEffect(() => {
    let aktualne = true;
    void pobierzZasoby(orgId)
      .then((z) => {
        if (aktualne) setZasoby(z);
      })
      .catch(() => undefined);
    return () => {
      aktualne = false;
    };
  }, [orgId]);

  const wgraj = useCallback(
    async (plik: File): Promise<ZasobDruku | null> => {
      setBladZasobu(null);
      try {
        const { asset } = await wgrajZasob(orgId, {
          nazwa: plik.name.replace(/\.[^.]+$/, '').slice(0, 120) || 'obraz',
          mime: plik.type,
          base64: await plikDoBase64(plik),
        });
        // Duplikat wraca tym samym identyfikatorem, więc lista nie może dostać
        // drugiego wpisu — inaczej po dwóch wgraniach tego samego logo wybór
        // pokazywałby dwie pozycje wskazujące ten sam plik.
        setZasoby((biezace) =>
          biezace.some((z) => z.id === asset.id) ? biezace : [asset, ...biezace]
        );
        return asset;
      } catch (err) {
        setBladZasobu(err instanceof PrintCoreError ? err.message : 'Nie udało się wgrać obrazu');
        return null;
      }
    },
    [orgId]
  );

  // ── Zmiana układu + autozapis ──────────────────────────────────────────────
  const zmien = useCallback((nowy: PrintLayout) => {
    setHistoria((h) => (h ? dopiszHistorie(h, nowy) : h));
  }, []);

  useEffect(() => {
    if (!layout || wczytuje) return;
    const moj = ++licznikZapisu.current;
    const t = setTimeout(async () => {
      setStanZapisu({ rodzaj: 'zapisuje' });
      try {
        const stan = await zapiszSzkicUkladu(orgId, szablon.slug, layout);
        // Odpowiedź starszego zapisu nie ma prawa nadpisać stanu nowszego —
        // przy szybkim przeciąganiu żądania wracają nie po kolei.
        if (moj !== licznikZapisu.current) return;
        setStanZapisu({ rodzaj: 'zapisano' });
        // Ostrzeżenie o wyjeździe poza etykietę odświeża się RAZEM z zapisem,
        // bo inaczej wisiałoby na liście warstw długo po tym, jak blok wrócił
        // na swoje miejsce — a ostrzeżenie, które kłamie, uczy je ignorować.
        setPozaObszarem(stan.out_of_bounds ?? []);
      } catch (err) {
        if (moj !== licznikZapisu.current) return;
        setStanZapisu({
          rodzaj: 'blad',
          komunikat: err instanceof PrintCoreError ? err.message : 'Autozapis nie powiódł się',
        });
      }
    }, OPOZNIENIE_AUTOZAPISU);
    return () => clearTimeout(t);
  }, [layout, orgId, szablon.slug, wczytuje]);

  // ── Dopasowanie płótna do okna ─────────────────────────────────────────────
  const wymiary = useMemo(
    () => (layout ? wymiaryMm(layout.media) : { szer: 100, wys: 60 }),
    [layout]
  );

  useEffect(() => {
    const el = obszarPlotna.current;
    if (!el || !auto) return;
    const przelicz = () =>
      setSkala(skalaDopasowania({ szer: el.clientWidth, wys: el.clientHeight }, wymiary));
    przelicz();
    // ResizeObserver, nie `window.resize`: kolumna zmienia szerokość także
    // wtedy, gdy okno stoi w miejscu (zwinięty pasek boczny powłoki).
    const obs = new ResizeObserver(przelicz);
    obs.observe(el);
    return () => obs.disconnect();
  }, [auto, wymiary]);

  const ustawSkale = (s: number) => {
    setAuto(false);
    setSkala(Math.min(SKALA_MAX, Math.max(SKALA_MIN, Math.round(s * 100) / 100)));
  };

  // ── Operacje na węzłach ────────────────────────────────────────────────────
  const przesun = (id: string, dx: number, dy: number) => {
    if (!layout) return;
    zmien(zmienWezel(layout, id, (n) => przesunWezel(n, dx, dy)));
  };

  const zmienZaznaczony = (fn: (n: LayoutNode) => LayoutNode) => {
    if (!layout || !zaznaczony) return;
    zmien(zmienWezel(layout, zaznaczony, fn));
  };

  const usunZaznaczony = () => {
    if (!layout || !zaznaczony) return;
    zmien(usunWezel(layout, zaznaczony));
    setZaznaczony(null);
  };

  const wstaw = (co: DoWstawienia) => {
    if (!layout) return;
    if (co === 'tabela') {
      const nowe = wezlyTabeli(szablon.variables ?? [], 5, 5, Math.max(20, wymiary.szer - 10));
      zmien({ ...layout, nodes: [...layout.nodes, ...nowe] });
      setZaznaczony(nowe[nowe.length - 1].id);
      setZakladka('wlasciwosci');
      return;
    }
    const node = co === 'chwila' ? wezelChwili('data_godzina', 5, 5) : nowyWezel(co, 5, 5);
    zmien({ ...layout, nodes: [...layout.nodes, node] });
    setZaznaczony(node.id);
    setZakladka('wlasciwosci');
  };

  const przestaw = (id: string, kierunek: -1 | 1) => {
    if (!layout) return;
    const i = layout.nodes.findIndex((n) => n.id === id);
    const j = i + kierunek;
    if (i < 0 || j < 0 || j >= layout.nodes.length) return;
    const nodes = [...layout.nodes];
    [nodes[i], nodes[j]] = [nodes[j], nodes[i]];
    zmien({ ...layout, nodes });
  };

  // ── Publikacja ─────────────────────────────────────────────────────────────
  const publikuj = async () => {
    if (!layout) return;
    setPublikuje(true);
    setBladPublikacji(null);
    try {
      const wynik = await opublikujUklad(orgId, szablon.slug, wersjaBazowa, layout);
      setWersjaBazowa(wynik.template.version);
      onOpublikowano();
    } catch (err) {
      if (err instanceof PrintCoreError) {
        if (err.kod === 'version_conflict') {
          const aktualna = err.dane.current_version;
          setBladPublikacji(
            `Ktoś opublikował wersję ${typeof aktualna === 'number' ? aktualna : '?'} przed tobą. ` +
              'Wczytaj aktualny stan i scal zmiany — inaczej nadpisałbyś cudzą pracę.'
          );
        } else if (err.kod === 'limit_planu') {
          setBladPublikacji(komunikatLimituPlanu(err) ?? err.message);
        } else if (err.kod === 'override_kind_conflict') {
          setBladPublikacji(
            'Ten wzór ma aktywne odstępstwo Liquid. Najpierw przywróć wzór domyślny, ' +
              'potem projektuj układ — obie postacie nie mogą istnieć naraz.'
          );
        } else {
          setBladPublikacji(err.message);
        }
      } else {
        setBladPublikacji('Publikacja nie powiodła się');
      }
    } finally {
      setPublikuje(false);
    }
  };

  const porzuc = async () => {
    if (!confirm('Porzucić szkic i wrócić do ostatnio opublikowanego układu?')) return;
    try {
      await porzucSzkicUkladu(orgId, szablon.slug);
      await wczytaj();
    } catch (err) {
      setBladPublikacji(
        err instanceof PrintCoreError ? err.message : 'Nie udało się porzucić szkicu'
      );
    }
  };

  // ── Podgląd ────────────────────────────────────────────────────────────────
  const odswiezPodglad = async () => {
    if (!layout) return;
    setBladPodgladu(null);
    setOdswiezaPodglad(true);
    try {
      const wynik = await podgladUkladu(orgId, szablon.slug, {
        target: celPodgladu,
        zrodlo: 'szkic',
        layout,
      });
      setPodglad({
        url: wynik.url ?? (wynik.blob ? URL.createObjectURL(wynik.blob) : null),
        tekst: wynik.tekst,
      });
    } catch (err) {
      setBladPodgladu(err instanceof PrintCoreError ? err.message : 'Podgląd nie powiódł się');
    } finally {
      setOdswiezaPodglad(false);
    }
  };

  const zaznaczonyWezel =
    layout && zaznaczony ? (layout.nodes.find((n) => n.id === zaznaczony) ?? null) : null;

  const idPozaObszarem = useMemo(
    () => new Set(pozaObszarem.map((b) => b.nodeId).filter((id): id is string => Boolean(id))),
    [pozaObszarem]
  );

  const przeszkodyPublikacji = niezwiazane.length > 0 || pozaObszarem.length > 0;

  return (
    <Modal
      open
      onClose={onZamknij}
      title={`Projekt wydruku: ${szablon.name}`}
      size="full"
      // Pełna wysokość, bo trzy kolumny mają się rozciągnąć na okno, a nie
      // skurczyć do treści. `min-h-0` na ciele jest tu warunkiem, nie ozdobą:
      // bez niego dziecko z `flex-1` nie kurczy się poniżej swojej treści
      // i siatka wypycha stopkę poza ekran.
      className="h-[calc(100vh-2rem)]"
      bodyClassName="min-h-0 overflow-hidden p-0"
      footer={
        <ModalFooter>
          <span className="mr-auto flex items-center gap-2 text-xs text-aurora-subtle">
            {stanZapisu.rodzaj === 'zapisuje' && 'Zapisywanie szkicu…'}
            {stanZapisu.rodzaj === 'zapisano' && 'Szkic zapisany'}
            {stanZapisu.rodzaj === 'blad' && (
              <span className="text-aurora-danger">Autozapis: {stanZapisu.komunikat}</span>
            )}
          </span>
          <Button variant="ghost" onClick={() => void porzuc()}>
            Porzuć szkic
          </Button>
          <Button variant="secondary" onClick={onZamknij}>
            Zamknij
          </Button>
          <Button
            onClick={() => void publikuj()}
            loading={publikuje}
            disabled={!layout || przeszkodyPublikacji}
            title={
              przeszkodyPublikacji
                ? 'Publikacja zablokowana — zobacz ostrzeżenia nad paskiem narzędzi'
                : undefined
            }
          >
            Opublikuj
          </Button>
        </ModalFooter>
      }
    >
      {wczytuje && (
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      )}

      {bladWczytania && (
        <div className="p-6">
          <Alert variant="error">{bladWczytania}</Alert>
        </div>
      )}

      {layout && !wczytuje && (
        <div className="flex h-full min-h-0 flex-col">
          {/* Ostrzeżenia — pas nad paskiem narzędzi, zwinięty do jednej linii
              na komunikat. W poprzedniej wersji trzy `Alert`-y jeden pod drugim
              zjadały jedną trzecią wysokości okna, zanim cokolwiek narysowano. */}
          {(drift ||
            niezwiazane.length > 0 ||
            pozaObszarem.length > 0 ||
            bladPublikacji ||
            bladZasobu) && (
            <div className="shrink-0 space-y-1 border-b border-aurora-border px-3 py-2">
              {bladPublikacji && <Alert variant="error">{bladPublikacji}</Alert>}
              {bladZasobu && <Alert variant="error">{bladZasobu}</Alert>}
              {niezwiazane.length > 0 && (
                <Alert variant="error">
                  Publikacja zablokowana: układ nie używa wymaganych zmiennych —{' '}
                  <span className="mono">{niezwiazane.join(', ')}</span>. Etykieta bez wymaganego
                  pola to etykieta do wyrzucenia.
                </Alert>
              )}
              {pozaObszarem.length > 0 && (
                <Alert variant="warning">
                  <span className="inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {pozaObszarem.length === 1
                      ? 'Jeden blok wyjeżdża poza obszar zadruku'
                      : `${pozaObszarem.length} bloki wyjeżdżają poza obszar zadruku`}{' '}
                    (oznaczone w warstwach). Szkic zapisuje się dalej — publikacja jest zablokowana,
                    bo drukarka utnie to bez ostrzeżenia.
                  </span>
                </Alert>
              )}
              {drift && (
                <Alert variant="warning">
                  Wzór w katalogu zmienił się od czasu zasiewu tego układu — sprawdź, czy wszystkie
                  pola są nadal na miejscu.
                </Alert>
              )}
            </div>
          )}

          <PasekNarzedzi
            onWstaw={wstaw}
            onCofnij={() => setHistoria((h) => (h ? cofnij(h) : h))}
            onPonow={() => setHistoria((h) => (h ? ponow(h) : h))}
            mozeCofnac={historia !== null && mozeCofnac(historia)}
            mozePonowic={historia !== null && mozePonowic(historia)}
            siatka={layout.edytor?.przyciaganie ?? true}
            onSiatka={(wl) =>
              zmien({
                ...layout,
                edytor: { siatkaMm: layout.edytor?.siatkaMm ?? 1, przyciaganie: wl },
              })
            }
            skala={skala}
            onSkala={ustawSkale}
            onDopasuj={() => setAuto(true)}
            wymiary={wymiary}
          />

          <div className="grid min-h-0 flex-1 grid-cols-[minmax(200px,240px)_minmax(0,1fr)_minmax(280px,360px)]">
            {/* Warstwy */}
            <div className="flex min-h-0 flex-col border-r border-aurora-border">
              <NaglowekKolumny>Warstwy — kolejność rysowania</NaglowekKolumny>
              <div className="min-h-0 flex-1 overflow-auto">
                <PanelWarstw
                  layout={layout}
                  zaznaczony={zaznaczony}
                  pozaObszarem={idPozaObszarem}
                  onZaznacz={setZaznaczony}
                  onZmienKolejnosc={przestaw}
                  onPrzelacz={(id, pole) =>
                    zmien(zmienWezel(layout, id, (n) => ({ ...n, [pole]: !n[pole] || undefined })))
                  }
                />
              </div>
            </div>

            {/* Płótno */}
            <div
              ref={obszarPlotna}
              className="min-h-0 overflow-auto bg-aurora-bg-subtle p-6"
              // Płótno kotwiczymy do GÓRY, nie do środka w pionie: przy
              // powiększeniu większym niż okno wyśrodkowanie w pionie chowa
              // górną krawędź etykiety pod krawędzią kontenera i nie da się
              // do niej doscrollować.
            >
              <Plotno
                layout={layout}
                zaznaczony={zaznaczony}
                onZaznacz={setZaznaczony}
                onPrzesun={przesun}
                skala={skala}
              />
            </div>

            {/* Właściwości / Podgląd */}
            <div className="flex min-h-0 flex-col border-l border-aurora-border">
              <div className="flex shrink-0 gap-1 border-b border-aurora-border p-1.5">
                {(
                  [
                    ['wlasciwosci', 'Właściwości'],
                    ['podglad', 'Podgląd'],
                  ] as const
                ).map(([klucz, etykieta]) => (
                  <button
                    key={klucz}
                    type="button"
                    onClick={() => setZakladka(klucz)}
                    className={cn(
                      'flex-1 rounded-ids-lg px-3 py-1.5 text-xs font-medium transition-colors',
                      zakladka === klucz
                        ? 'bg-aurora-brand/15 text-aurora-brand'
                        : 'text-aurora-muted hover:bg-aurora-bg-subtle hover:text-aurora-text'
                    )}
                  >
                    {etykieta}
                    {klucz === 'wlasciwosci' && zaznaczonyWezel && (
                      <Badge className="ml-2">{zaznaczonyWezel.kind}</Badge>
                    )}
                  </button>
                ))}
              </div>

              <div className="min-h-0 flex-1 overflow-auto">
                {zakladka === 'wlasciwosci' ? (
                  <PanelWlasciwosci
                    node={zaznaczonyWezel}
                    zmienne={szablon.variables ?? []}
                    cele={szablon.targets ?? ['PDF']}
                    zasoby={zasoby}
                    onWgrajZasob={wgraj}
                    onZmien={zmienZaznaczony}
                    onUsun={usunZaznaczony}
                  />
                ) : (
                  <PanelPodgladu
                    cele={szablon.targets ?? ['PDF']}
                    cel={celPodgladu}
                    onCel={setCelPodgladu}
                    podglad={podglad}
                    blad={bladPodgladu}
                    pracuje={odswiezaPodglad}
                    onOdswiez={() => void odswiezPodglad()}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
